/**
 * Agent Cube 新建向导本地缓存（localStorage）。
 * 与 ModelManagementCenter 的 `_dbl_model_create_cache` 分离，避免结构校验冲突。
 * 仅缓存「未 save 草稿」前的 Step1~3 业务态，按 gameCode + userId 隔离。
 */
import { WizardStep } from '../constant';
import type {
  IStepFieldConfigState,
  IStepSelectTablesState,
  IStepYamlDraftState,
} from '../types';

/**
 * 向导整包快照（与 `toWizardPersistSnapshot` / `fromWizardPersistSnapshot` 成对）。
 * `selectTables.dataBaseOptions` 落盘恒为 []，恢复后由 Step1 重新拉库表 hydrate。
 */
export interface IAgentCreateWizardSnapshot {
  current: WizardStep;
  selectTables: IStepSelectTablesState;
  fieldConfig: IStepFieldConfigState;
  yamlDraft: IStepYamlDraftState;
  /** 与 `useModelAgentCreateWizard` 内 fieldConfigLoadKeyRef 一致 */
  fieldConfigLoadKey: string;
}

/** localStorage 根结构：租户 + 用户 + 向导快照 */
export interface IAgentCreateCacheData {
  gameCode: string;
  userId: string;
  timestamp: number;
  payload: IAgentCreateWizardSnapshot;
}

const CACHE_CONFIG = {
  /** 与旧版 ModelCreate 缓存一致，避免长期占用 localStorage */
  EXPIRY_HOURS: 24,
  STORAGE_KEY: '_dbl_model_agent_create_cache',
} as const;

/** 是否存在值得恢复的向导进度（未保存草稿前） */
export const hasAgentCreateCacheableProgress = (
  snapshot: IAgentCreateWizardSnapshot,
): boolean => {
  const { selectTables, fieldConfig, yamlDraft, current } = snapshot;
  // Step1：库 / Schema / 表任一已选即视为有操作，需离开提示与可恢复缓存
  const hasStepOneSelection =
    Boolean(selectTables.databaseValue) ||
    Boolean(selectTables.schemaValue) ||
    (selectTables.tablesValue?.length ?? 0) > 0;
  const hasFields = (fieldConfig.fieldColumns?.length ?? 0) > 0;
  const hasYaml = Boolean(yamlDraft.yamlText?.trim());
  return (
    hasStepOneSelection || hasFields || hasYaml || current !== WizardStep.One
  );
};

export class AgentCreateCacheManager {
  private readonly cacheKey: string;

  constructor(cacheKey: string = CACHE_CONFIG.STORAGE_KEY) {
    this.cacheKey = cacheKey;
  }

  /** 写入或覆盖整包缓存；timestamp 每次 save 刷新 */
  save(data: Omit<IAgentCreateCacheData, 'timestamp'>): void {
    try {
      const newData: IAgentCreateCacheData = {
        ...data,
        timestamp: Date.now(),
      };
      localStorage.setItem(this.cacheKey, JSON.stringify(newData));
    } catch (error) {
      console.error('Agent 创建向导缓存保存失败:', error);
      this.clear();
    }
  }

  /**
   * 读取并校验；结构非法、过期或无有效进度时主动 clear，避免脏数据反复 restore。
   */
  load(): IAgentCreateCacheData | null {
    try {
      const raw = localStorage.getItem(this.cacheKey);
      if (!raw) {
        return null;
      }
      const parsed: IAgentCreateCacheData = JSON.parse(raw);
      if (!this.validateCacheData(parsed)) {
        this.clear();
        return null;
      }
      if (this.isExpired(parsed.timestamp)) {
        this.clear();
        return null;
      }
      if (!hasAgentCreateCacheableProgress(parsed.payload)) {
        this.clear();
        return null;
      }
      return parsed;
    } catch (error) {
      console.error('Agent 创建向导缓存加载失败:', error);
      this.clear();
      return null;
    }
  }

  /** Step3 save 草稿成功或用户放弃恢复时由调用方触发 */
  clear(): void {
    try {
      localStorage.removeItem(this.cacheKey);
    } catch (error) {
      console.error('Agent 创建向导缓存清理失败:', error);
    }
  }

  private isExpired(timestamp: number): boolean {
    const expiryMs = CACHE_CONFIG.EXPIRY_HOURS * 60 * 60 * 1000;
    return Date.now() > timestamp + expiryMs;
  }

  private validateCacheData(data: unknown): data is IAgentCreateCacheData {
    if (!data || typeof data !== 'object') {
      return false;
    }
    const d = data as IAgentCreateCacheData;
    return (
      typeof d.gameCode === 'string' &&
      typeof d.userId === 'string' &&
      typeof d.timestamp === 'number' &&
      d.payload !== null &&
      typeof d.payload === 'object'
    );
  }
}

export const createAgentCreateCacheManager = (): AgentCreateCacheManager =>
  new AgentCreateCacheManager();
